import { useState } from 'react'
import { IconMessageReport, IconX } from '@tabler/icons-react'
import html2canvas from 'html2canvas'
import { useTranslation } from 'react-i18next'
import {
  ActionIcon,
  Button,
  Group,
  Image,
  Modal,
  Stack,
  Textarea,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useAuth } from '@/contexts/AuthContext'
import { sendReport } from '@/routes/report'

export default function ReportButton() {
  const [opened, setOpened] = useState(false)
  const [screenshot, setScreenshot] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  const [sending, setSending] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const { me } = useAuth()
  const { t } = useTranslation()
  const { colorScheme } = useMantineColorScheme()
  const isDark = colorScheme === 'dark'

  if (!me) {
    return null
  }

  const captureAndOpen = async () => {
    try {
      // Hide tooltip before capturing
      setTooltipOpen(false)
      await new Promise((r) => setTimeout(r, 200))
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: window.devicePixelRatio > 1 ? 1 : window.devicePixelRatio,
        ignoreElements: (el) => el.id === 'report-fab',
      })
      setScreenshot(canvas.toDataURL('image/png'))
      setOpened(true)
    } catch {
      notifications.show({
        message: t('report.error'),
        color: 'red',
      })
    }
  }

  const handleClose = () => {
    setOpened(false)
    setScreenshot(null)
    setComment('')
  }

  const handleSend = async () => {
    if (!comment.trim() && !screenshot) {
      return
    }
    setSending(true)
    try {
      await sendReport({
        screenshot: screenshot || '',
        comment,
        url: window.location.href,
        user_agent: navigator.userAgent,
      })
      notifications.show({
        message: t('report.success'),
        color: 'green',
      })
      handleClose()
    } catch {
      notifications.show({
        message: t('report.error'),
        color: 'red',
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <div
        id="report-fab"
        style={{ position: 'fixed', bottom: 72, right: 24, zIndex: 200 }}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
      >
        <Tooltip label={t('report.tooltip')} position="left" multiline w={220} opened={tooltipOpen}>
          <ActionIcon
            size="xl"
            radius="xl"
            variant="filled"
            color="green"
            onClick={captureAndOpen}
            style={{
              boxShadow: isDark
                ? '0 4px 16px rgba(0, 255, 0, 0.3)'
                : '0 4px 16px rgba(0, 100, 0, 0.2)',
            }}
          >
            <IconMessageReport size={24} />
          </ActionIcon>
        </Tooltip>
      </div>

      <Modal opened={opened} onClose={handleClose} title={t('report.title')} size="lg" centered>
        <Stack gap="md">
          {screenshot && (
            <div style={{ position: 'relative' }}>
              <ActionIcon
                size="sm"
                radius="xl"
                variant="filled"
                color="red"
                onClick={() => setScreenshot(null)}
                style={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
              >
                <IconX size={14} />
              </ActionIcon>
              <Image
                src={screenshot}
                alt="Screenshot"
                mah={300}
                fit="contain"
                radius="sm"
                style={{
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                }}
              />
            </div>
          )}
          <Textarea
            label={t('report.comment')}
            placeholder={t('report.comment_placeholder')}
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            minRows={3}
            autosize
          />
          <Group justify="flex-end">
            <Button variant="light" color="red" onClick={handleClose}>
              {t('report.discard')}
            </Button>
            <Button color="green" onClick={handleSend} loading={sending}>
              {t('report.send')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
